import React, { useCallback, useEffect, useState, useRef } from "react";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { MAIN_URL } from "../../../Configurations/Urls";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { formatLeaveDateDisplay } from "../../../lib/formatLeaveDateDisplay";
import { fetchLeaveTypes } from "../../../Apis/Leave-api";
import { fetchInternLeaves } from "../../../Apis/InternManagement";
import {
  applyClientToolbarFilters,
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const DEFAULT_COLUMNS = [
  {
    field: "intern_code",
    label: "intern_code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "intern_name",
    label: "intern_name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "approval_date",
    label: "approval_date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "approved_by",
    label: "Approved By",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "rejected_by",
    label: "Rejected By",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
  prev_page: null,
  next_page: null,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_person_leave_id",
  sortOrder: "desc",
};

const INTERN_LEAVE_TOOLBAR_FILTERS = [
  { urlKey: "leave_type_id", field: "leave_type" },
  { urlKey: "leave_status", field: "leave_status" },
];

const LEAVE_STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
];

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();
  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;
    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);
    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);
    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;
      if (requiresValue && filterValue) {
        if (["equals", "not_equals", "in", "not_in"].includes(operator)) {
          // Keep casing for status / FK labels (backend may compare case-insensitively)
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
          if (filterValue.length === 1) {
            filterValue = filterValue[0];
          }
        } else if (
          ["between", "not_between", "date_between", "date_not_between"].includes(
            operator
          )
        ) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }
      filters.push({ field, operator, value: filterValue });
    }
  });
  return filters;
};

function InternLeaveList() {
  const [leaves, setLeaves] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [leaveTypes, setLeaveTypes] = useState([]);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
  };

  useEffect(() => {
    if (hasInitializedURL.current) return;
    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    fetchLeaveTypes(org.organization_id)
      .then((data) => {
        if (cancelled) return;
        setLeaveTypes(asOptionList(data, ["leavetypes", "data"]));
      })
      .catch((error) => {
        console.error("Failed to load intern leave types", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  // Load table configuration: metadata for columns, then saved layout
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }
      try {
        let columnsFromMetadata = [];
        const metaRes = await fetch(
          `${MAIN_URL}/api/metadata/intern_leaves`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (metaRes.ok) {
          const meta = await metaRes.json();
          const fields = meta.fields || [];
          const displayFields = fields.filter((f) => f.is_display_field);
          const useFields = displayFields.length > 0 ? displayFields : fields;
          columnsFromMetadata = useFields
            .filter((f) => f.field_key !== "organization_person_id")
            .map((f) => ({
              field: f.field_key,
              label:
                f.label ||
                (f.field_key === "approved_by"
                  ? "Approved By"
                  : f.field_key === "rejected_by"
                    ? "Rejected By"
                    : f.field_key),
              visible: true,
              sortable: true,
              filterable: !!f.is_filterable,
              width: 150,
              field_type: f.field_type,
            }));
        }
        

        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `intern_leaves_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );
          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              const labelByField = Object.fromEntries(
                columnsFromMetadata.map((c) => [c.field, c.label])
              );
              setConfigColumns(
                serverCfg.columns.map((col) => ({
                  ...col,
                  label:
                    labelByField[col.field] ||
                    (col.field === "approved_by"
                      ? "Approved By"
                      : col.field === "rejected_by"
                        ? "Rejected By"
                        : col.label),
                }))
              );
            } else if (columnsFromMetadata.length > 0) {
              setConfigColumns(columnsFromMetadata);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else if (columnsFromMetadata.length > 0) {
            setConfigColumns(columnsFromMetadata);
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        } else if (columnsFromMetadata.length > 0) {
          setConfigColumns(columnsFromMetadata);
        } else {
          setConfigColumns(DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadTableConfiguration();
  }, [org?.organization_id]);

  // Load data via POST filters (metadata-driven)
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();
    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          INTERN_LEAVE_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_person_leave_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/intern-leaves/filters`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
                prev_page: result.pagination.prev_page ?? null,
                next_page: result.pagination.next_page ?? null,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          const oldData = await fetchInternLeaves(org.organization_id);
          data = applyClientToolbarFilters(
            oldData?.internleaves?.data || oldData?.internleaves || oldData?.data || [],
            searchParams,
            INTERN_LEAVE_TOOLBAR_FILTERS
          );
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const b = data.map((item) => {
          const startDate = formatLeaveDateDisplay(item?.leave_start_date);
          const endDate = formatLeaveDateDisplay(item?.leave_end_date);
          const approval_date = formatLeaveDateDisplay(item?.approval_date);
          const rejection_date = formatLeaveDateDisplay(item?.rejection_date);

          return {
            ...item,
            id: item.organization_person_leave_id,
            intern_name: item?.intern_name ?? item?.full_name ?? "",
            intern_code: item?.intern_code ?? item?.person_code ?? "",
            profile_image_url: item?.profile_image_url || "",
            leave_start_date: startDate,
            leave_end_date: endDate,
            approval_date: approval_date,
            rejection_date: rejection_date,
            leave_type: item?.leave_type ?? "",
            leave_category: item?.leave_category ?? "",
            leave_reason: item?.leave_reason ?? "",
            leave_reason_type: item?.leave_reason_type ?? "",
            leave_start_time: item?.leave_start_time ?? "",
            leave_end_time: item?.leave_end_time ?? "",
            leave_rejection_reason: item?.leave_rejection_reason ?? "",
            total_leave_hours: item?.total_leave_hours ?? "",
            person_remarks: item?.person_remarks ?? "",
            approved_by: item?.approved_by ?? "",
            rejected_by: item?.rejected_by ?? "",
          };
        });

        setLeaves(b);
      } catch (err) {
        if (err.name === "AbortError") return;
        if (err.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch intern leaves:", err);
          toast.error("Failed to load intern leaves");
          setLeaves([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  let deleteInternLeave = async (id) => {
    try {
      const org_id = org.organization_id;
      const response = await fetch(
        `${MAIN_URL}/api/organizations/${org_id}/intern-leaves/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        toast.success(data.message ?? "Intern leave deleted successfully.");
        setSearchParams((prev) => new URLSearchParams(prev));
      } else {
        const errorMessage =
          data.message ||
          data.errors?.[Object.keys(data.errors || {})[0]]?.[0] ||
          "Failed to delete Intern Leave";
        toast.error(errorMessage);
        console.warn("Deletion error:", response.status, data);
      }
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Delete failed:", error);
      toast.error(error?.message || "Failed to delete Intern Leave");
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/intern-leaves/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      console.log("Successfully deleted units-types with id:", id);
      return Promise.resolve();
    } catch (error) {
      console.error("Delete failed:", error);
      return Promise.reject(error);
    }
  };

  const handleEdit = useCallback(
    (item) => {
      navigate(`/organization/intern/intern-leaves/edit/${item.id}`);
    },

    [navigate]
  );



  const handleShow = useCallback(
    (item) => {
      navigate(`/organization/intern/intern-leaves/view/${item.id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Intern Leave"}
        btnName={"Add Intern Leave"}
        Data={leaves}
        delete_action={"INTERN_LEAVE_DELETE"}
        tableHeaders={[
          {
            name: "Employee Name",
            value_key: "fullName",
            textStyle: "capitalize",
          },
          { name: "Leave Starts", value_key: "startDate" },
          { name: "Leave Ends", value_key: "endDate" },
          {
            name: "Employee Remarks",
            value_key: "employee_remarks",
            textStyle: "capitalize",
          },
          { name: "Leave Status", value_key: "leave_status" },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Intern Leaves",
          "Intern Leaves",
          "Add Leaves",
          "Intern Leaves",
        ]}
        Route={"/organization/intern/intern-leaves"}
        setData={setLeaves}
        DeleteFunc={deleteInternLeave}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="intern_leaves"
          primaryKey="organization_person_leave_id"
          heading="Intern Leaves"
          data={leaves}
          sortname="organization_person_leave_id"
          showActions={true}
          Route="/organization/intern/intern-leaves"
          DeleteFunc={handleDelete}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="intern_leaves"
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Leave Type"
                urlKey="leave_type_id"
                allLabel="All leave types"
                options={leaveTypes}
                valueKey="organization_leave_type_id"
                labelKey="leave_type_name"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarFilterSelect
                label="Leave Status"
                urlKey="leave_status"
                allLabel="All leave statuses"
                options={LEAVE_STATUS_OPTIONS}
                valueKey="value"
                labelKey="label"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          edit_delete_action={["INTERN_LEAVE_DELETE", "INTERN_LEAVE_EDIT"]}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default InternLeaveList;
